import { jsx, jsxs } from "react/jsx-runtime";
import { Stack } from "braid-design-system";
import { RowPanel } from "../../../../panels/RowPanel/RowPanel.mjs";
import { getPanelStyle } from "../../utils/getPanelStyle/getPanelStyle.mjs";
import { TrackingContext, getProductIsSelectedLoading } from "../../../GridLayout/GridLayout.mjs";
import { GridRow } from "../Grid/GridRow/GridRow.mjs";
import { useContext } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import { rowPanel } from "../../SharedLayout.css.mjs";
const PRODUCT_ID = {
  LITE_PRODUCT_ID: "32c4f41d-241f-4755-9d28-febd8e5e1575",
  URGENT_ADD_ON_ANZ_PRODUCT_ID: "e989c2e5-6d27-4fd6-aeb5-21971cbaaff8",
  URGENT_ADD_ON_ASIA_PRODUCT_ID: "51f8e58b-3bbd-43f6-beab-66c9384c82d7"
};
const getAddOnBannerOverride = ({
  parentProductPurchaseState,
  addOn
}) => {
  const urgentAddOnProductIds = [
    PRODUCT_ID.URGENT_ADD_ON_ANZ_PRODUCT_ID,
    PRODUCT_ID.URGENT_ADD_ON_ASIA_PRODUCT_ID
  ];
  const isUrgentAddOnOfCurrentProduct = parentProductPurchaseState === "PURCHASED" && addOn.purchaseState === "PURCHASABLE" && urgentAddOnProductIds.includes(addOn.id);
  if (isUrgentAddOnOfCurrentProduct) {
    return {
      type: "limited-time-offer",
      variant: "narrow"
    };
  }
  return addOn.banner;
};
const UpsellsWrapper = ({
  children,
  selectedParentProductId,
  upgradePaths,
  addOns
}) => {
  const { trackEvent } = useContext(TrackingContext);
  useDeepCompareEffect(() => {
    if (upgradePaths.length > 0 || addOns.length > 0) {
      trackEvent(
        {
          type: "upsells",
          action: "displayed",
          addOns,
          upgradePaths
        },
        { selectedParentProductId }
      );
    }
  }, [upgradePaths, addOns, selectedParentProductId]);
  return /* @__PURE__ */ jsx(
    TrackingContext.Provider,
    {
      value: {
        trackEvent: (data, inputMeta) => trackEvent(data, { ...inputMeta, selectedParentProductId })
      },
      children
    }
  );
};
const Upsells = ({
  singleParentProduct,
  addOnProductsList,
  upsellProducts,
  prefixContent,
  selectedProductIds,
  loadingProductIds,
  onProductSelect,
  gridRowClassName
}) => {
  const hasUpsells = singleParentProduct && (upsellProducts.length > 0 || addOnProductsList.length > 0);
  if (!hasUpsells) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    UpsellsWrapper,
    {
      addOns: addOnProductsList.map(({ addOn }) => addOn),
      upgradePaths: upsellProducts,
      selectedParentProductId: singleParentProduct.id,
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        (prefixContent == null ? void 0 : prefixContent.upgrades) && upsellProducts.length ? /* @__PURE__ */ jsx(GridRow, { className: gridRowClassName, children: prefixContent.upgrades }) : null,
        upsellProducts.map((product) => {
          const { isSelected, isLoading } = getProductIsSelectedLoading({
            selectedProductIds,
            loadingProductIds,
            product
          });
          return /* @__PURE__ */ jsx(
            GridRow,
            {
              className: [
                rowPanel,
                getPanelStyle({
                  panel: product.panel,
                  isSelected,
                  isCurrentProduct: false,
                  productType: product.type
                }),
                gridRowClassName ?? ""
              ],
              children: /* @__PURE__ */ jsx(
                RowPanel,
                {
                  product,
                  isSelected,
                  isLoading,
                  onSelect: onProductSelect
                }
              )
            },
            product.id
          );
        }),
        (prefixContent == null ? void 0 : prefixContent.addOns) && addOnProductsList.length ? /* @__PURE__ */ jsx(GridRow, { className: gridRowClassName, children: prefixContent.addOns }) : null,
        addOnProductsList.map(({ addOn, parentProductPurchaseState }) => {
          addOn.banner = getAddOnBannerOverride({
            parentProductPurchaseState,
            addOn
          });
          const isCurrentProduct = addOn.purchaseState === "PURCHASED";
          const { isSelected, isLoading } = getProductIsSelectedLoading({
            selectedProductIds,
            loadingProductIds,
            product: addOn,
            forceSelected: isCurrentProduct
          });
          return /* @__PURE__ */ jsx(
            GridRow,
            {
              className: [
                rowPanel,
                getPanelStyle({
                  panel: addOn.panel,
                  isSelected,
                  isCurrentProduct,
                  productType: addOn.type
                }),
                gridRowClassName ?? ""
              ],
              children: /* @__PURE__ */ jsx(
                RowPanel,
                {
                  product: addOn,
                  isSelected,
                  isLoading,
                  onSelect: onProductSelect
                }
              )
            },
            addOn.id
          );
        })
      ] })
    }
  );
};
export {
  PRODUCT_ID,
  Upsells
};
